import { useEffect } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, ArrowRight, Check, Phone, TriangleAlert } from "lucide-react";
import { coverage } from "./coverageData";

const LOGO_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_practical-chatterjee-6/artifacts/t28q9iq2_ChatGPT%20Image%20Jul%2028%2C%202025%2C%2012_36_46%20PM.png";

export default function CoveragePage() {
  const { id } = useParams();
  const item = coverage.find((entry) => entry.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
    const meta = document.querySelector("meta[name='description']");
    const previousTitle = document.title;
    const previousDescription = meta?.getAttribute("content");
    if (item) {
      document.title = `${item.label} Insurance Guide | PolicySalah`;
      meta?.setAttribute("content", `${item.label} insurance explained in plain English — what it covers, what to watch in the fine print, and honest FAQs from PolicySalah, independent risk advisors.`);
    }
    return () => {
      document.title = previousTitle;
      if (meta && previousDescription) meta.setAttribute("content", previousDescription);
    };
  }, [id]);

  if (!item) {
    return (
      <div className="site-shell">
        <header className="nav">
          <Link to="/" className="brand" data-testid="brand-home-link">
            <span className="brand-logo-frame"><img className="brand-logo" src={LOGO_URL} alt="PolicySalah logo" /></span>
            <span>Policy<span>Salah</span><small>A brand of Renu Associates</small></span>
          </Link>
        </header>
        <main className="guide-missing section-pad" data-testid="coverage-not-found">
          <h1>That cover world doesn’t exist yet.</h1>
          <Link className="back-link" to="/" data-testid="not-found-home-link"><ArrowLeft size={15} /> Back to the coverage universe</Link>
        </main>
      </div>
    );
  }

  const Icon = item.icon;
  const index = coverage.findIndex((entry) => entry.id === item.id) + 1;

  return (
    <div className="site-shell" data-testid={`coverage-page-${item.id}`}>
      <header className="nav">
        <Link to="/" className="brand" data-testid="brand-home-link">
          <span className="brand-logo-frame"><img className="brand-logo" src={LOGO_URL} alt="PolicySalah logo" /></span>
          <span>Policy<span>Salah</span><small>A brand of Renu Associates</small></span>
        </Link>
        <nav className="nav-links">
          <Link to="/" className="back-link" data-testid="guide-back-link"><ArrowLeft size={15} /> Back to home</Link>
        </nav>
      </header>
      <main>
        <section className={`guide-hero ${item.color}`}>
          <div className="detail-icon"><Icon size={28} /></div>
          <span className="detail-label guide-label">{item.label} / 0{index}</span>
          <h1 data-testid="guide-title">{item.title}</h1>
          <p>{item.copy}</p>
          <div className="detail-tags">{item.tags.map((tag) => <span key={tag}>#{tag}</span>)}</div>
        </section>
        <section className="guide-body">
          <div data-testid="guide-includes">
            <h2 className="guide-h2">What it usually covers</h2>
            {item.includes.map((point) => (
              <div className="include-item" key={point}><Check size={17} /><span>{point}</span></div>
            ))}
          </div>
          <div data-testid="guide-watchouts">
            <h2 className="guide-h2">Watch the fine print</h2>
            {item.watch.map((point) => (
              <div className="watch-item" key={point}><TriangleAlert size={16} /><span>{point}</span></div>
            ))}
          </div>
        </section>
        <section className="guide-faqs" data-testid="guide-faqs">
          <h2 className="guide-h2">Questions people actually ask</h2>
          {item.faqs.map((faq) => (
            <div className="faq-item" key={faq.q}>
              <h3>{faq.q}</h3>
              <p>{faq.a}</p>
            </div>
          ))}
        </section>
        <section className="guide-cta">
          <div>
            <h2>Is {item.label.toLowerCase()} your risk?</h2>
            <p>A 30-minute conversation can show you what your current cover misses.</p>
          </div>
          <div className="guide-cta-actions">
            <a className="light-button" href="tel:+916378630748" data-testid="guide-call-button"><Phone size={15} /> Call +91 63786 30748</a>
            <a className="detail-action" href="/#contact" data-testid="guide-contact-link">Contact us <ArrowRight size={15} /></a>
          </div>
        </section>
      </main>
      <footer className="footer">
        <span>© 2024 PolicySalah</span>
        <span>Independent risk & insurance advisors</span>
        <a href="mailto:namaste@policysalah.com" data-testid="footer-email-link">namaste@policysalah.com</a>
      </footer>
    </div>
  );
}
